import { Component, Input, OnInit, ViewChild, OnDestroy } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { IonContent, NavController, Platform } from "@ionic/angular";
import { Navigator } from "src/app/services/routing/navigator";
import { SuccessMessageService } from "src/app/services/message-provider/success-message.service";
import { CustomerModel } from "src/app/models/customer-model";
import { AuthenticationService } from "src/app/services/authentication/authentication.service";
import { ConfigService } from "src/app/services/config/config.service";
import {
  noWhitespaceOnlyValidator,
  PASSWORD_PATTERN,
} from "src/app/services/customFormValidators";
import { EventsService } from "src/app/services/event/event.service";
import { UserSessionService } from "src/app/services/user-session/user-session.service";
import { LogoutService } from "src/app/services/logout/logout.service";

@Component({
  selector: "app-change-password",
  templateUrl: "./change-password.component.html",
  styleUrls: ["./change-password.component.scss"],
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
  @ViewChild(IonContent, { static: false }) content: IonContent;

  // @Input() playerMigrationCheck: boolean | string;
  @Input() callBackFn: () => void;
  public error: any = null;
  public passwordForm: FormGroup;

  public customer: CustomerModel;

  public currentPassword: AbstractControl;
  public passwordGroup: AbstractControl;
  public newPassword: AbstractControl;
  public confirmPassword: AbstractControl;
  public securityGroup: AbstractControl;
  public securityAnswer: AbstractControl;
  public secondSecurityAnswer: AbstractControl;
  public playerForceResetCheck: boolean;
  public customerWillRedirect: boolean = false;
  public isValidated: boolean = false;
  public securityQuestionsRequiredMFA: boolean;
  public callNextFunctionsOnClose: boolean = false;
  private dismissTimeoutId: NodeJS.Timeout | null = null;

  constructor(
    // public logger: Logger,
    public navCtrl: NavController,
    public authService: AuthenticationService,
    public userSession: UserSessionService,
    public successMessageService: SuccessMessageService,
    protected config: ConfigService,
    public events: EventsService,
    public navigator: Navigator,
    public platform: Platform,
    private logoutService: LogoutService
  ) {
    this.successMessageService.clearMessage();

    this.userSession.getCustomer().subscribe((customer) => {
      if (customer) {
        this.customer = customer;
        this.setupSecurityFormControls();
      }
    });

    //setting up playerForceResetCheck flag
    // getting it from storage, to avoid loosing context on page reload
    this.playerForceResetCheck =  JSON.parse(localStorage.getItem('userSession.loginInfo')).forcePasswordReset;
    // this.playerForceResetCheck = !!this.playerMigrationCheck;
  }

  ngOnInit() {
    localStorage.setItem('forcePasswordReset', 'true');
    this.securityQuestionsRequiredMFA = this.config.getConfig().FEATURE_SECURITY_QUESTIONS_REQUIRED == "1" || this.config.getConfig().FEATURE_SECURITY_QUESTIONS_REQUIRED == null ? true : false;

    this.passwordForm = new FormGroup({
      currentPassword: new FormControl("", [Validators.required]),
      passwordGroup: new FormGroup(
        {
          newPassword: new FormControl("", [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(PASSWORD_PATTERN),
          ]),
          confirmPassword: new FormControl("", [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(PASSWORD_PATTERN),
          ]),
        },
        this.passwordMatchValidator
      ),
      securityGroup: new FormGroup({}),
    });

    this.currentPassword = this.passwordForm.get("currentPassword");
    this.passwordGroup = this.passwordForm.get("passwordGroup");
    this.newPassword = this.passwordGroup.get("newPassword");
    this.confirmPassword = this.passwordGroup.get("confirmPassword");

    this.securityGroup = this.passwordForm.get("securityGroup");
  }

  setupSecurityFormControls() {
    if (!this.playerForceResetCheck && this.securityQuestionsRequiredMFA && this.customer) {
      const hasFirstQuestion = !!this.customer.securityQuestion;
      const hasSecondQuestion = !!this.customer.secondSecurityQuestion;

      if (hasFirstQuestion || hasSecondQuestion) {
        // Create new security form controls based on which questions exist
        const securityControls: any = {};

        if (hasFirstQuestion) {
          securityControls.securityAnswer = new FormControl("", [
            Validators.required,
            noWhitespaceOnlyValidator,
          ]);
        }

        if (hasSecondQuestion) {
          securityControls.secondSecurityAnswer = new FormControl("", [
            Validators.required,
            noWhitespaceOnlyValidator,
          ]);
        }

        // Replace the empty security group with one containing the appropriate controls
        this.passwordForm.setControl('securityGroup', new FormGroup(securityControls));
        this.securityGroup = this.passwordForm.get("securityGroup");

        // Set up references to the individual controls if they exist
        if (hasFirstQuestion) {
          this.securityAnswer = this.securityGroup.get("securityAnswer");
        }
        if (hasSecondQuestion) {
          this.secondSecurityAnswer = this.securityGroup.get('secondSecurityAnswer');
        }
      }
    }
  }

  passwordMatchValidator(g: FormGroup) {
    return g.get("newPassword").value === g.get("confirmPassword").value
      ? null
      : { mismatch: true };
  }

  ionViewDidLeave() {
    if (this.customerWillRedirect == true) {
      this.events.publish("event:appInit:next");
    }
  }

  onSubmit() {
    this.error = null;
    this.successMessageService.clearMessage();

    //Seprating out playMigration call for changePassword
    if (this.playerForceResetCheck) {
      this.authService
        .callForceChangePassword(this.passwordForm.value)
        .subscribe({
          next: (response: any) => {
            if (response && !!response.result) {
              this.error = null;
              this.successMessageService.setMessage(
                "Your password has been changed."
              );
              this.isValidated = true;
              localStorage.removeItem('forcePasswordReset');

              this.content.scrollToTop(10);
              this.customerWillRedirect = true;
              if (this.callBackFn) {
                this.callNextFunctionsOnClose = true;
                if (this.dismissTimeoutId) {
                  clearTimeout(this.dismissTimeoutId);
                }
                this.dismissTimeoutId = setTimeout(() => {
                  this.navigator.dismiss();
                  this.callBackFn();
                }, 3000);
              } else {
                this.navigator.dismissAll();
                this.events.publish("event:appInit:next");
              }
            }
          },
          error: (error) => {
            console.error("callForceChangePassword ", error);

            switch (error.getErrorCode().toUpperCase()) {
              case "CURRENT_PASS_FAIL":
                error.setErrorMessage(
                  "You have incorrectly entered your current password."
                );
                this.currentPassword.setErrors({ CURRENT_PASS_FAIL: true });
                this.currentPassword.markAsDirty();
                this.currentPassword.markAsTouched();
                break;
              case "CURRENT_PASS_NO_MATCH":
                error.setErrorMessage(
                  "Your new password can not match your current password."
                );
                this.passwordGroup.setErrors({ CURRENT_PASS_NO_MATCH: true });
                this.passwordGroup.markAsDirty();
                this.passwordGroup.markAsTouched();
                break;
              case "SECURE_NO_MATCH":
                error.setErrorMessage(
                  "You have incorrectly answered your security questions."
                );
                this.securityGroup.setErrors({ SECURE_NO_MATCH: true });
                this.securityGroup.markAsDirty();
                this.securityGroup.markAsTouched();
                break;
            }
            this.error = error;
            this.content.scrollToTop(10);
          }
      });
    } else if(!this.securityQuestionsRequiredMFA) {
      //no sec questions
      this.authService.callChangePasswordNoSec(this.passwordForm.value, this.customer.email).subscribe({
        next: (response: any) => {
          if (response && !!response.result) {
            this.error = null;
            this.successMessageService.setMessage(
              "Your password has been changed."
            );
            this.isValidated = true;
            localStorage.removeItem('forcePasswordReset');

            this.content.scrollToTop();
          }
        },
        error: (error) => {
          switch (error.getErrorCode().toUpperCase()) {
            case "CURRENT_PASS_FAIL":
              error.setErrorMessage(
                "You have incorrectly entered your current password."
              );
              this.currentPassword.setErrors({ CURRENT_PASS_FAIL: true });
              this.currentPassword.markAsDirty();
              this.currentPassword.markAsTouched();
              break;
            case "CURRENT_PASS_NO_MATCH":
              error.setErrorMessage(
                "Your new password can not match your current password."
              );
              this.passwordGroup.setErrors({ CURRENT_PASS_NO_MATCH: true });
              this.passwordGroup.markAsDirty();
              this.passwordGroup.markAsTouched();
              break;
          }
          this.error = error;
          this.content.scrollToTop(10);
        }
      });
    }
    else {
      //Noraml ChangePassword Call
      this.authService.callChangePassword(this.passwordForm.value).subscribe({
        next: (response: any) => {
          if (response && !!response.result) {
            this.error = null;
            this.successMessageService.setMessage(
              "Your password has been changed."
            );
            this.isValidated = true;
            localStorage.removeItem('forcePasswordReset');
            this.content.scrollToTop();
          }
        },
        error: (error) => {

          switch (error.getErrorCode().toUpperCase()) {
            case "CURRENT_PASS_FAIL":
              error.setErrorMessage(
                "You have incorrectly entered your current password."
              );
              this.currentPassword.setErrors({ CURRENT_PASS_FAIL: true });
              this.currentPassword.markAsDirty();
              this.currentPassword.markAsTouched();
              break;
            case "CURRENT_PASS_NO_MATCH":
              error.setErrorMessage(
                "Your new password can not match your current password."
              );
              this.passwordGroup.setErrors({ CURRENT_PASS_NO_MATCH: true });
              this.passwordGroup.markAsDirty();
              this.passwordGroup.markAsTouched();
              break;
            case "SECURE_NO_MATCH":
              error.setErrorMessage(
                "You have incorrectly answered your security questions."
              );
              this.securityGroup.setErrors({ SECURE_NO_MATCH: true });
              if (this.securityAnswer) {
                this.securityAnswer.setErrors({ SECURE_NO_MATCH: true });
              }
              if (this.secondSecurityAnswer) {
                this.secondSecurityAnswer.setErrors({ SECURE_NO_MATCH: true });
              }
              break;
          }
          this.error = error;
          this.content.scrollToTop(10);
        }
      });
    }
  }

  closeModal = () => {
      if(this.dismissTimeoutId){
        clearTimeout(this.dismissTimeoutId);
      }
    if(this.playerForceResetCheck && !this.isValidated){

      if(this.callNextFunctionsOnClose){
        this.navigator.dismiss();
        this.callBackFn();
      } else {
        this.navigator.dismiss();
        return this.logoutService.onLogout({reason:'INCOMPLETE_FORCE_RESET_PASSWORD'});
      }
      // this.navigator.navigateTo(
      //   "LogoutPage",
      //   { forceModal: true },
      //   { reason: "INCOMPLETE_FORCE_RESET_PASSWORD" }
      // );
    } else if (this.customerWillRedirect) {
      this.navigator.dismiss();
      if (this.callBackFn) {
        this.navigator.dismiss();
        this.callBackFn();
      } else {
        if (this.callBackFn) {
          this.callNextFunctionsOnClose = true;
            this.navigator.dismiss();
            this.callBackFn();
        } else {
          this.navigator.dismiss();
          this.events.publish("event:appInit:next");
        }
      }
    }
  };

  ngOnDestroy() {
    if (this.dismissTimeoutId) {
      clearTimeout(this.dismissTimeoutId);
    }
  }
}
